import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { NotificationsMenu } from '../notifications';
import { dismissAllNotifications } from '../../store/redux/actions/notificationActions';
import UserMenu from './UserMenu';
import LanguageSwitch from './LanguageSwitch';
import TopBarPendingActions from '../pendingActions/TopBarPendingActions';
import { NavigationModule, NavigationPage } from './types';

interface TopBarProps {
  /** Callback to open/close the mobile sidebar */
  onMobileMenuClick: () => void;
  /**
   * The modules the SIDEBAR is drawing: `useFilteredNavigationState().modules` from MasterLayout,
   * already filtered by role, company module and page config, demo grants, country, tier and the
   * per-feature flags. Search offers these pages and nothing else (CO-853).
   */
  modules: NavigationModule[];
}

interface RootState {
  user: {
    companyName: string;
    userId: string;
  };
}

interface SearchablePage {
  path: string;
  /** Translated page title, as the sidebar shows it. */
  title: string;
  /** Translated owner trail: the module, then the sub-module when the page sits in one. */
  trail: string[];
  /** The sidebar's "Soon" badge. A badge only, never a filter: see `NavigationPage.comingSoon`. */
  comingSoon: boolean;
  /** Folded text the query is matched against: the title in the UI language, then its key. */
  titleText: string;
  /** Folded owner text, same two spellings. */
  trailText: string;
}

const MAX_RESULTS = 8;

/** Lower-cased and stripped of Latin accents, so "parametres" finds "Paramètres". Arabic is unchanged. */
const fold = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Most nav titles are their own English text ("Leave Calendar"); a few are dotted keys
 * ("automationGrowth.title"). A dotted key is not something a person types, and matching it would
 * make the word "title" find that page, so only a readable key is searched alongside the translation.
 */
const isReadableKey = (key: string): boolean => !/^[\w-]+(\.[\w-]+)+$/.test(key);

/**
 * Top navigation bar with search, notifications, language switch, and user menu.
 *
 * Integrations:
 * - NotificationsMenu: Uses existing SignalR-connected component
 * - UserMenu: Shows real user data with profile/settings/logout
 * - LanguageSwitch: Integrates with existing i18n system
 * - Search: page search over the filtered navigation handed down by MasterLayout
 */
const TopBar: React.FC<TopBarProps> = ({ onMobileMenuClick, modules }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const { companyName, userId } = useSelector((state: RootState) => state.user);
  const language = i18n.language;

  /* CO-853. The list used to be built from the raw registry with its own role, tier and preview
     checks, and knew nothing of the company's module and page switches: a base employee could find
     "Payroll Reports", and every tenant could find Recruitment and the demo pages. It is now built
     from the SAME filtered modules the sidebar draws, so the two can never disagree, and no rule is
     restated here. Do not add a filter to this loop: a page that must not be searchable must not be
     in the sidebar either, and the place for that is `useFilteredNavigation`.

     Consequences worth knowing:
     - Sub-module pages (the whole Settings tree) are searchable. They never were.
     - A demo page shows up for a company that was granted it, and for no other.
     - While the company config is still loading the list holds what the sidebar would: real pages
       only, no demo surface.
     - Titles are the sidebar's keys, translated. No key is added for search. */
  const searchablePages = useMemo(() => {
    const pages: SearchablePage[] = [];
    const seen = new Set<string>();
    const add = (page: NavigationPage, trailKeys: string[]) => {
      // A path the filter could not resolve (`:userId` before the user id arrives) cannot be opened.
      if (seen.has(page.path) || page.path.includes(':')) return;
      seen.add(page.path);
      const title = t(page.title);
      const trail = trailKeys.map((key) => t(key));
      pages.push({
        path: page.path,
        title,
        trail,
        comingSoon: !!page.comingSoon,
        titleText: fold(isReadableKey(page.title) ? `${title} ${page.title}` : title),
        trailText: fold([...trail, ...trailKeys.filter(isReadableKey)].join(' ')),
      });
    };
    modules.forEach((module) => {
      module.pages.forEach((page) => add(page, [module.title]));
      (module.subModules ?? []).forEach((sub) => {
        sub.pages.forEach((page) => add(page, [module.title, sub.title]));
      });
    });
    return pages;
    // `language` is listed because `t` keeps its identity across some language switches.
  }, [modules, t, language]);

  // Best first: a title that starts with the query, then a title that contains it, then a page
  // whose module or sub-module matches. The sort is stable, so ties keep sidebar order.
  const trimmedQuery = searchQuery.trim();
  const searchResults = useMemo(() => {
    if (!trimmedQuery) return [];
    const query = fold(trimmedQuery);
    const rank = (page: SearchablePage): number => {
      if (page.titleText.startsWith(query)) return 0;
      if (page.titleText.includes(query)) return 1;
      if (page.trailText.includes(query)) return 2;
      return -1;
    };
    return searchablePages
      .map((page) => ({ page, rank: rank(page) }))
      .filter((entry) => entry.rank >= 0)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.page);
  }, [trimmedQuery, searchablePages]);

  const openResult = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) openResult(searchResults[0].path);
  };

  const searchLabel = t('common:Search pages...') || 'Search pages...';
  const panelOpen = showSearchResults && trimmedQuery.length > 0;

  return (
    <header className="h-16 min-h-16 flex-shrink-0 bg-card border-b border-border flex items-center justify-between px-4 md:px-6">
      {/* Left Section - Mobile Menu + Search */}
      <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1 min-w-0">
        {/* Mobile Menu Button */}
        <button
          onClick={onMobileMenuClick}
          className="md:hidden p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          title={t('common:Open menu') || 'Open menu'}
        >
          <Icon name="Menu" size={20} className="text-muted-foreground" />
        </button>

        {/* Search. The panel closes when focus leaves this WHOLE block, not when it leaves the input,
            so a result reached with Tab stays on screen long enough to be opened. */}
        <div
          className="relative flex-1 min-w-0 max-w-xl"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setShowSearchResults(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowSearchResults(false);
          }}
        >
          <form onSubmit={handleSearch} role="search">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              type="text"
              data-testid="topbar-search-input"
              aria-label={searchLabel}
              placeholder={searchLabel}
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => {
                setShowSearchResults(true);
              }}
              className="w-full ps-10 pe-4 py-2 border border-border hover:border-foreground/30 rounded-lg text-sm text-foreground bg-background transition-all focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary focus:outline-none placeholder:text-muted-foreground"
            />
          </form>

          {/* Search results. `start-0` keeps the panel under the input in both directions.
              `onMouseDown` keeps focus in the input while the pointer is down anywhere in the panel
              (a result, or the scrollbar of a long list), so the block's blur handler does not close
              it before the click lands. Results are opened on click, which the keyboard fires too. */}
          {panelOpen && (
            <div
              data-testid="topbar-search-results"
              onMouseDown={(e) => e.preventDefault()}
              className="absolute top-full start-0 w-full mt-1 bg-popover rounded-lg shadow-lg border border-border z-50 max-h-80 overflow-y-auto"
            >
              {searchResults.length === 0 ? (
                /* Nothing matched. Said out loud because search now leaves out every page this user
                   cannot open: without a line here, a query for one of those looks like a dead box. */
                <p
                  role="status"
                  data-testid="topbar-search-empty"
                  className="px-4 py-3 text-sm text-muted-foreground"
                >
                  {t('common:No results')}
                </p>
              ) : (
                searchResults.map((result) => (
                  <button
                    key={result.path}
                    type="button"
                    data-testid="topbar-search-result"
                    data-path={result.path}
                    onClick={() => openResult(result.path)}
                    className="w-full px-4 py-3 text-start hover:bg-muted transition-colors border-b border-border last:border-0 focus-visible:outline-none focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  >
                    <p className="text-sm font-medium text-foreground flex items-center gap-2 min-w-0">
                      <span className="truncate">{result.title}</span>
                      {result.comingSoon && (
                        <span className="shrink-0 text-[10px] font-medium bg-warning/10 text-warning px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          {t('Soon')}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 min-w-0">
                      {result.trail.map((label, index) => (
                        <React.Fragment key={`${index}-${label}`}>
                          {index > 0 && (
                            <Icon name="ChevronRight" size={12} className="shrink-0 rtl:rotate-180" aria-hidden="true" />
                          )}
                          <span className="truncate">{label}</span>
                        </React.Fragment>
                      ))}
                    </p>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Notifications, Language, User Menu */}
      <div className="flex items-center space-x-2 md:space-x-4 rtl:space-x-reverse">
        {/* Pending actions count — hidden when the user has nothing waiting */}
        <TopBarPendingActions />

        {/* Notifications - Using existing mowazaf component with SignalR */}
        <NotificationsMenu
          companyName={companyName}
          userId={userId}
          dismissAll={dismissAllNotifications}
          onNavigate={(path: string) => navigate(path)}
        />

        {/* Language Switch */}
        <LanguageSwitch />

        {/* User Menu */}
        <UserMenu />
      </div>
    </header>
  );
};

export default TopBar;
